// Content for the Zemantics Catalyst Suite (source: the Catalyst Research, Curriculum and Workforce
// brochures and the Catalyst Suite exhibition banner). Shared by the Products page and the SEO layer.

export interface CatalystCapability {
  title: string;
  text: string;
}

export interface CatalystProduct {
  id: 'research' | 'curriculum' | 'workforce';
  name: string;
  logo: string;
  logoAlt: string;
  strapline: string;
  headline: string;
  summary: string;
  suiteBlurb: string;
  builtFor: string[];
  capabilities: CatalystCapability[];
}

export const SUITE = {
  name: 'Zemantics Catalyst',
  logo: '/Assets/Products/catalyst-suite-logo.png',
  logoAlt: 'Zemantics Catalyst - The Catalyst Suite',
  tagline: 'Turn evidence into better decisions.',
  description: 'AI-powered decision support for research portfolios, academic programs and workforce opportunities.',
  flow: ['Evidence', 'Recommendation', 'Human decision'],
  email: 'info@zemanticsllc.com',
};

export const PRODUCTS: CatalystProduct[] = [
  {
    id: 'research',
    name: 'Catalyst Research',
    logo: '/Assets/Products/catalyst-research-logo.png',
    logoAlt: 'Zemantics Catalyst Research logo',
    strapline: 'Continue. Redirect. Advance. Stop.',
    headline: 'From research data to a clear next decision.',
    summary: 'Connects reports, systems and teams to portfolio decisions, early warnings and commercialization potential.',
    suiteBlurb: 'Support research investment decisions and explore pathways to commercialization.',
    builtFor: [
      'Corporate R&D and innovation leaders',
      'Research sponsors and funders',
      'University and tech-transfer teams',
      'National labs and research institutes',
    ],
    capabilities: [
      { title: 'Portfolio Intelligence', text: 'Compare projects by evidence strength, strategic fit, horizon and next investment.' },
      { title: 'Project Decision Profile', text: 'Continue, redirect, advance or stop, linked to evidence and human review.' },
      { title: 'Additional Value', text: 'Spot commercialization, licensing and partnership opportunities.' },
    ],
  },
  {
    id: 'curriculum',
    name: 'Catalyst Curriculum',
    logo: '/Assets/Products/catalyst-curriculum-logo.png',
    logoAlt: 'Zemantics Catalyst Curriculum logo',
    strapline: 'Review. Align. Refine. Advance.',
    headline: 'Keep every course improving, with faculty in charge.',
    summary: 'Connects course content and changing evidence to proposed improvements for committee approval.',
    suiteBlurb: 'Support ongoing program review and faculty-led curriculum improvement.',
    builtFor: [
      'University deans and academic leaders',
      'Department chairs and program directors',
      'Curriculum committees and faculty teams',
      'Academic quality and review teams',
    ],
    capabilities: [
      { title: 'Curriculum Intelligence', text: 'Review syllabi and outcomes. Find gaps and overlaps each term.' },
      { title: 'Industry & Faculty Fit', text: 'Weigh market signals: teach now, upskill, or partner.' },
      { title: 'Faculty Decision Support', text: 'Draft module and assessment changes for committee approval.' },
    ],
  },
  {
    id: 'workforce',
    name: 'Catalyst Workforce',
    logo: '/Assets/Products/catalyst-workforce-logo.png',
    logoAlt: 'Zemantics Catalyst Workforce logo',
    strapline: 'Monitor. Connect. Plan. Adapt.',
    headline: 'Align programs with labor demand and funding.',
    summary: 'Connects labor-market trends, employer input and funding signals to actionable planning.',
    suiteBlurb: 'Use labor-market signals to plan credentials, employer partnerships and funding.',
    builtFor: [
      'University deans and academic leaders',
      'Provosts and planning teams',
      'Workforce and employer partnership leaders',
      'Program directors and curriculum committees',
    ],
    capabilities: [
      { title: 'Labor-Market Intelligence', text: 'Sourced job and skill changes that reveal emerging demand.' },
      { title: 'Employer & Program Pathways', text: 'Verified employer needs, partnerships and placement leads.' },
      { title: 'Funding & Readiness', text: 'Grant pathways with eligibility evidence, deadlines and owners.' },
    ],
  },
];

export const PRINCIPLES: CatalystCapability[] = [
  { title: 'Source-linked', text: 'Every recommendation points back to its sources.' },
  { title: 'Assumptions stated', text: 'Assumptions and evidence gaps are shown with each result.' },
  { title: 'Human decisions', text: 'Authorized people approve, edit or decline.' },
];

export const CATALYST_FAQ: { q: string; a: string }[] = [
  {
    q: 'What is the Zemantics Catalyst Suite?',
    a: 'Three AI-powered decision-support products, Catalyst Research, Catalyst Curriculum and Catalyst Workforce, for research portfolios, academic programs and workforce opportunities.',
  },
  {
    q: 'Does Catalyst make decisions automatically?',
    a: 'No. Catalyst gives evidence-linked recommendations with assumptions and gaps. Authorized people review, edit, approve or decline.',
  },
  {
    q: 'Who is Catalyst built for?',
    a: 'Research: R&D leaders, sponsors, university tech-transfer teams and national labs. Curriculum: deans, chairs, program directors and curriculum committees. Workforce: deans, provosts, planning and employer-partnership leaders.',
  },
  {
    q: 'What do we need to get started?',
    a: 'Research: permitted reports, agreements and milestones. Curriculum: catalogs, syllabi, program goals and learning outcomes. Workforce: one geography and occupation cluster, official labor data, and your program list and funder rules.',
  },
  {
    q: 'Does Catalyst need student data?',
    a: 'No. Identifiable student data is not required to get started.',
  },
  {
    q: 'How do we begin?',
    a: 'Start small: a pilot on 1-3 projects or programs, or one geography and occupation cluster. Email info@zemanticsllc.com or use the contact page.',
  },
];
